import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  type MonthSalariesResponse,
  SALARY_MONTHS,
  salaryMonthLabel,
  toCents,
} from '@k-management/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { moneyOf, PeopleViews, peopleKeys, SalaryStatePill } from './people-ui.js';

/**
 * Stipendi del mese (07/10/2026): the nets of a month for all the employees in
 * force, as the payslips arrive from the labour consultant; «Resta da pagare»
 * in total is what to transfer.
 */
export function MonthSalariesPage() {
  usePageTitle('Stipendi del mese');
  const queryClient = useQueryClient();
  const canWrite = useCan('persone.write');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [nets, setNets] = useState<Record<string, string>>({});
  const data = useQuery({
    queryKey: peopleKeys.month(year, month),
    queryFn: () => apiRequest<MonthSalariesResponse>(`/people/salaries/${year}/${month}`),
  });
  useEffect(() => {
    if (data.data)
      setNets(Object.fromEntries(data.data.rows.map((row) => [row.employeeId, row.net ?? ''])));
  }, [data.data]);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<MonthSalariesResponse>(`/people/salaries/${year}/${month}`, {
        method: 'PUT',
        body: {
          items: (data.data?.rows ?? []).map((row) => ({
            employeeId: row.employeeId,
            net: moneyOf(nets[row.employeeId] ?? ''),
          })),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: peopleKeys.all });
    },
  });
  const rows = data.data?.rows ?? [];
  const unreadable = rows.some(
    (row) => (nets[row.employeeId] ?? '').trim() !== '' && moneyOf(nets[row.employeeId]!) === null,
  );
  // What is left with the nets as typed, before saving.
  const left = (row: (typeof rows)[number]) => {
    const net = moneyOf(nets[row.employeeId] ?? '');
    return net === null ? null : toCents(net) - toCents(row.advances) - toCents(row.paid);
  };
  const sum = (pick: (row: (typeof rows)[number]) => number | null) =>
    fromCents(rows.reduce((total, row) => total + (pick(row) ?? 0), 0));
  const years = [
    now.getFullYear() + 1,
    now.getFullYear(),
    now.getFullYear() - 1,
    now.getFullYear() - 2,
  ];

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <p className="muted small">
            <Link to="/organizzazione/persone">Persone</Link> › Stipendi del mese
          </p>
          <h1>Stipendi del mese</h1>
          <p className="muted">
            Il netto in busta di ogni dipendente in forza nel mese. «Resta da pagare», in totale, è
            quanto c’è da bonificare. Caricando le paghe del mese in «Costo del personale» i netti
            arrivano da soli dai cedolini.
          </p>
        </div>
      </div>
      <PeopleViews current="stipendi" />
      <div className="row month-picker">
        <select
          aria-label="Mensilità"
          value={month}
          onChange={(event) => setMonth(Number(event.target.value))}
        >
          {SALARY_MONTHS.map((item) => (
            <option key={item} value={item}>
              {salaryMonthLabel(item)}
            </option>
          ))}
        </select>
        <select
          aria-label="Anno"
          value={year}
          onChange={(event) => setYear(Number(event.target.value))}
        >
          {years.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
      {data.isError && <p className="error">{data.error.message}</p>}
      {data.data && rows.length === 0 && (
        <p className="muted">Nessun dipendente in forza in questo mese.</p>
      )}
      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Dipendente</th>
                <th className="numeric">Netto in busta</th>
                <th className="numeric">Acconti</th>
                <th className="numeric">Saldo pagato</th>
                <th className="numeric">Resta da pagare</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.employeeId}>
                  <td>
                    <Link to={`/organizzazione/persone/${row.employeeId}`}>{row.name}</Link>
                  </td>
                  <td className="numeric">
                    {canWrite ? (
                      <DecimalInput
                        aria-label={`Netto di ${row.name}`}
                        className="month-net"
                        value={nets[row.employeeId] ?? ''}
                        onChange={(value) => setNets({ ...nets, [row.employeeId]: value })}
                        decimals={[2, 2]}
                      />
                    ) : row.net === null ? (
                      '—'
                    ) : (
                      formatMoney(row.net)
                    )}
                  </td>
                  <td className="numeric">
                    {Number(row.advances) ? formatMoney(row.advances) : '—'}
                  </td>
                  <td className="numeric">{Number(row.paid) ? formatMoney(row.paid) : '—'}</td>
                  <td className="numeric">
                    {left(row) === null ? '—' : formatMoney(fromCents(left(row)!))}
                  </td>
                  <td>
                    <SalaryStatePill state={row.state} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Totale</th>
                <th className="numeric">
                  {formatMoney(
                    sum((row) => {
                      const net = moneyOf(nets[row.employeeId] ?? '');
                      return net === null ? null : toCents(net);
                    }),
                  )}
                </th>
                <th className="numeric">{formatMoney(sum((row) => toCents(row.advances)))}</th>
                <th className="numeric">{formatMoney(sum((row) => toCents(row.paid)))}</th>
                <th className="numeric">{formatMoney(sum(left))}</th>
                <th />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {save.error && <p className="error">{save.error.message}</p>}
      {save.isSuccess && !save.isPending && <p className="small">Salvato.</p>}
      {canWrite && rows.length > 0 && (
        <div className="actions">
          <button
            type="button"
            className="primary"
            disabled={save.isPending || unreadable}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      )}
    </section>
  );
}
